/* eslint-disable */
import React, { useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericTabs, { TabOption } from "@ui-components/Tabs/GenericTabs";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import PropagationTabContent from "./PropagationTabContent";
import { 
  createInboundPropagationRuleCluster, 
  createOutboundPropagationRuleCluster,
  buildInboundPropagationRuleClusterPayload,
  buildOutboundPropagationRuleClusterPayload,
} from "../../Cluster/request/request";
import { 
  createInboundPropagationRule, 
  createOutboundPropagationRule,
  buildInboundPropagationRulePayload,
  buildOutboundPropagationRulePayload
} from "../../request/request";

const TABS: TabOption[] = [
  { label: "Inbound", value: "inbound" },
  { label: "Outbound", value: "outbound" },
];

interface CPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: any) => void;
  mode?: "add" | "edit";
  initialValues?: any;
  title?: React.ReactNode;
  badge?: React.ReactNode;
  tag?: string;
  bgpNeighborId?: number;
  reflectorId?: number; // Add reflectorId prop
  clusterId?: number; // Add clusterId for cluster mode
  isSubmitting?: boolean;
  getAccessTokenSilently?: () => Promise<string>; // Add token function for cluster mode
  hasExistingConfig?: boolean; // Add flag to indicate if config exists
}

const defaultTabState = {
  ruleName: "",
  policy: "",
  policySelection: "dynamic",
  routingTableTagName: "",
  routingTableTagValue: "",
  nextHopSelection: "dynamic",
  nextHopTagName: "",
  nextHopTagValue: "",
  routingTables: [], // Add routing tables array
  selectedVpc: "", // Add VPC selection
};

const CPModal: React.FC<CPModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode = "add",
  initialValues = {},
  title = (
    <span className="flex items-center gap-2 font-semibold">
      Configure propagation rule
    </span>
  ),
  badge,
  tag,
  bgpNeighborId,
  reflectorId,
  clusterId,
  isSubmitting = false,
  getAccessTokenSilently,
  hasExistingConfig = false,
}) => {
  const [activeTab, setActiveTab] = useState<string>("inbound");
  
  // Initialize state with proper handling of initial values
  const [inbound, setInbound] = useState(() => {
    if (initialValues.inbound) {
      // If we have initial values, use them directly without spreading defaults
      return initialValues.inbound;
    }
    // Only use defaults if no initial values are provided
    return { ...defaultTabState };
  });
  
  const [outbound, setOutbound] = useState(() => {
    if (initialValues.outbound) {
      // If we have initial values, use them directly without spreading defaults
      return initialValues.outbound;
    }
    // Only use defaults if no initial values are provided
    return { ...defaultTabState };
  });

  const isTelco = tag === "Telco";

  // Detect if we have existing data for each tab
  const hasExistingInboundData = React.useMemo(() => {
    if (!initialValues?.inbound) return false;
    
    // Check if we have any meaningful data in inbound
    const inboundData = initialValues.inbound;
    
    // Check if we have any non-default values that indicate existing configuration
    if (inboundData.vrfs && inboundData.vrfs.length > 0) {
      // Telco mode - check if VRF has data
      const vrf = inboundData.vrfs[0];
      return vrf.routingTables?.length > 0 || 
             vrf.enis?.length > 0 || 
             vrf.routeTargets?.length > 0 ||
             vrf.routingTableTagName ||
             vrf.routingTableTagValue ||
             vrf.nextHopTagName ||
             vrf.nextHopTagValue;
    } else {
      // Enterprise mode - check if we have routing tables, ENIs, or dynamic tag fields
      // Only check for actual data, not just selection changes
      return inboundData.routingTables?.length > 0 || 
             inboundData.enis?.length > 0 ||
             inboundData.routingTableTagName ||
             inboundData.routingTableTagValue ||
             inboundData.nextHopTagName ||
             inboundData.nextHopTagValue;
    }
  }, [initialValues?.inbound]);

  const hasExistingOutboundData = React.useMemo(() => {
    if (!initialValues?.outbound) return false;
    
    // Check if we have any meaningful data in outbound
    const outboundData = initialValues.outbound;
    return outboundData.subnets?.length > 0 || 
           outboundData.bgpCommunity || 
           outboundData.nexthopIPAddress;
  }, [initialValues?.outbound]);

  // Determine button text based on active tab and existing data
  const getButtonText = () => {
    if (isSubmitting) return "Loading...";
    
    if (activeTab === 'inbound') {
      // Use hasExistingConfig as fallback if data detection fails
      const hasData = hasExistingInboundData || (hasExistingConfig && activeTab === 'inbound');
      return hasData ? "Save" : "Save";
    } else {
      // Use hasExistingConfig as fallback if data detection fails
      const hasData = hasExistingOutboundData || (hasExistingConfig && activeTab === 'outbound');
      return hasData ? "Save" : "Save";
    }
  };

  // Update form state when initialValues change (when rule details are loaded)
  React.useEffect(() => {
    if (initialValues) {
      if (initialValues.inbound) {
        // Use initial values directly without spreading defaults
        setInbound(initialValues.inbound);
      }
      if (initialValues.outbound) {
        // Use initial values directly without spreading defaults
        setOutbound(initialValues.outbound);
      }
    }
  }, [initialValues]);

  // Helper function to get default state based on mode
  const getDefaultInboundState = () => {
    if (isTelco) {
      // For Telco mode, include a default VRF
      return {
        ...defaultTabState,
        vrfs: [{
          name: "Default VRF",
          routeTargets: [],
          newRouteTarget: "",
          routingTable: "",
          nextHopEni: "",
          policySelection: "dynamic",
          nextHopSelection: "dynamic",
          routingTables: [],
          enis: [],
          expanded: true,
        }]
      };
    }
    return { ...defaultTabState };
  };

  // Cleanup function to reset form state when modal is closed
  const handleClose = () => {
    // Reset form state to defaults when closing modal
    setInbound(getDefaultInboundState());
    setOutbound({ ...defaultTabState });
    setActiveTab("inbound"); // Reset to inbound tab
    onClose();
  };

  // Handle modal open/close state changes
  React.useEffect(() => {
    if (isOpen) {
      // Modal is opening - load initial values if they exist
      if (initialValues) {
        if (initialValues.inbound) {
          setInbound(initialValues.inbound);
        } else {
          setInbound(getDefaultInboundState());
        }
        if (initialValues.outbound) {
          setOutbound(initialValues.outbound);
        } else {
          setOutbound({ ...defaultTabState });
        }
      } else {
        // No initial values - reset to defaults
        setInbound(getDefaultInboundState());
        setOutbound({ ...defaultTabState });
      }
    }
  }, [isOpen, initialValues, isTelco]);

  const handleSave = async () => {
    console.log('CPModal handleSave called - activeTab:', activeTab, 'clusterId:', clusterId);
    
    // If clusterId is provided, handle cluster mode
    if (clusterId && getAccessTokenSilently) {
      try {
        const token = await getAccessTokenSilently();
        
        if (activeTab === 'inbound') {
          const payload = buildInboundPropagationRuleClusterPayload(inbound, tag || "Enterprise");
          
          // Use hasExistingConfig as fallback if data detection fails
          const shouldUpdate = hasExistingInboundData || (hasExistingConfig && activeTab === 'inbound');
          
          if (shouldUpdate) {
            // Update existing cluster inbound rule
            // Note: We need the inbound rule ID for cluster update - this should come from the API response
            if (onSubmit) {
              onSubmit({ 
                inbound, 
                outbound, 
                activeTab, 
                isUpdate: true, 
                isClusterMode: true,
                ruleType: 'inbound',
                payload 
              });
            }
          } else {
            // Create new cluster inbound rule
            if (onSubmit) {
              onSubmit({ 
                inbound, 
                outbound, 
                activeTab, 
                isUpdate: false, 
                isClusterMode: true,
                ruleType: 'inbound',
                payload 
              });
            } else {
              // Fallback: call API directly if no onSubmit handler
              await createInboundPropagationRuleCluster(token, clusterId, payload);
            }
          }
        } else if (activeTab === 'outbound') {
          console.log('CPModal processing outbound - outbound values:', outbound);
          const payload = buildOutboundPropagationRuleClusterPayload(outbound, tag || "Enterprise");
          console.log('CPModal outbound payload built:', payload);
          
          // For outbound, only update if we actually have existing outbound data
          // hasExistingConfig might be true due to inbound data, but we need outbound-specific data
          const shouldUpdate = hasExistingOutboundData;
          console.log('CPModal shouldUpdate:', shouldUpdate, 'hasExistingOutboundData:', hasExistingOutboundData, 'hasExistingConfig:', hasExistingConfig);
          
          if (shouldUpdate) {
            // Update existing cluster outbound rule
            console.log('CPModal updating existing outbound rule - calling onSubmit');
            if (onSubmit) {
              onSubmit({ 
                inbound, 
                outbound, 
                activeTab, 
                isUpdate: true, 
                isClusterMode: true,
                ruleType: 'outbound',
                payload 
              });
            }
          } else {
            // Create new cluster outbound rule
            console.log('CPModal creating new outbound rule - calling onSubmit');
            if (onSubmit) {
              onSubmit({ 
                inbound, 
                outbound, 
                activeTab, 
                isUpdate: false, 
                isClusterMode: true,
                ruleType: 'outbound',
                payload 
              });
            } else {
              console.log('CPModal no onSubmit handler - calling API directly');
              // Fallback: call API directly if no onSubmit handler
              await createOutboundPropagationRuleCluster(token, clusterId, payload);
            }
          }
        }
        
        // Close modal on success for cluster mode
        handleClose();
        return;
      } catch (error) {
        console.error('Error creating cluster propagation rule:', error);
        return; // Don't close modal on error
      }
    }
    
    // Standalone mode: Handle create vs update logic
    if (getAccessTokenSilently && bgpNeighborId) {
      try {
        const token = await getAccessTokenSilently();
        
        if (activeTab === 'inbound') {
          const payload = buildInboundPropagationRulePayload(bgpNeighborId, inbound, isTelco);
          
          // Only update if we actually have existing inbound data, not just any config
          const shouldUpdate = hasExistingInboundData;
          
          
          if (shouldUpdate) {
            // Update existing inbound rule
            if (onSubmit) {
              onSubmit({ 
                inbound, 
                outbound, 
                activeTab, 
                isUpdate: true, 
                ruleType: 'inbound',
                payload 
              });
            }
          } else {
            // Create new inbound rule
            console.log('CPModal creating new inbound rule');
            await createInboundPropagationRule(token, bgpNeighborId, payload);
          }
        } else if (activeTab === 'outbound') {
          const payload = buildOutboundPropagationRulePayload(bgpNeighborId, outbound, isTelco);
          
          // Only update if we actually have existing outbound data, not just any config
          const shouldUpdate = hasExistingOutboundData;
          
          if (shouldUpdate) {
            // Update existing outbound rule
            if (onSubmit) {
              onSubmit({ 
                inbound, 
                outbound, 
                activeTab, 
                isUpdate: true, 
                ruleType: 'outbound',
                payload 
              });
            }
          } else {
            // Create new outbound rule
            await createOutboundPropagationRule(token, bgpNeighborId, payload);
          }
        }
        
        handleClose();
        return;
      } catch (error) {
        console.error('Error handling propagation rule:', error);
        return; // Don't close modal on error
      }
    }
    
    // Fallback to original onSubmit if no token or bgpNeighborId
    if (onSubmit) {
      onSubmit({ inbound, outbound, activeTab });
    }
    handleClose();
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title={
        <div className="flex items-center gap-2 space-x-1">
          {title}
          {badge}
          {tag && (
            <GenericTag
              label={tag}
              variant={tag === "Telco" ? "warning" : "neutral"}
            />
          )}
        </div>
      }
    >
      <div className="flex flex-col items-center p-6 pt-2 gap-4 min-h-[400px] text-body max-h-[80vh] overflow-y-auto">
        <div className="w-full flex justify-between items-center">
          <GenericTabs
            tabs={TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
        <div className="w-full">
          {isSubmitting ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-500 mb-4"></div>
              <p className="text-sm text-semantic-text-secondary">Loading rule details...</p>
            </div>
          ) : (
            <PropagationTabContent
              values={activeTab === "inbound" ? inbound : outbound}
              setValues={activeTab === "inbound" ? setInbound : setOutbound}
              isTelco={isTelco}
              direction={activeTab as "inbound" | "outbound"}
              reflectorId={reflectorId}
              clusterId={clusterId}
              getAccessTokenSilently={getAccessTokenSilently}
            />
          )}
        </div>
        <div className="flex justify-end gap-4 mt-6 w-full">
          <GenericButton label="Cancel" variant="secondary" onClick={handleClose} disabled={isSubmitting} />
          <GenericButton
            label={getButtonText()}
            variant="brand"
            onClick={handleSave}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </GenericModal>
  );
};

export default CPModal;
